import { useId } from 'react';
import { IconProps } from '../types';
import classnames from 'classnames';

/**
 * Discover acceptance mark — the white card tile with the wordmark and orange swoosh, as the
 * scheme publishes it and as the Stripe Payment Element shows it.
 *
 * Artwork: datatrans/payment-logos `assets/cards/discover.svg` (fetched 2026-09-15), CC BY-SA 4.0 —
 * attribution: Datatrans AG. Unmodified apart from JSX attribute casing and a per-instance
 * gradient id. Shown to indicate that the card is accepted at
 * checkout, which is what the scheme's acceptance-mark rules provide the artwork for.
 */
export default function DiscoverIcon(props: IconProps) {
  const { className, style } = props;
  // Gradient ids are document-global; two Discover marks on one page (saved-card row + form slot)
  // must not share one.
  const gradientId = `${useId()}-discover-disc`;

  return (
    <svg className={classnames('h-4', className)} viewBox="0 0 120 80" fill="none" style={style}>
      <rect width="120" height="80" rx="4" fill="white" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M29 80H116.002C118.21 80 120 78.211 120 75.9957V48C120 48 87.8616 70.1063 29 80Z"
        fill="#E7792B"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M113.088 33.8624C113.088 30.7125 110.888 28.8951 107.053 28.8951H102.12V45.7197H105.443V38.9609H105.877L110.481 45.7197H114.571L109.202 38.6314C111.708 38.129 113.088 36.4383 113.088 33.8624ZM106.414 36.6411H105.443V31.5451H106.467C108.538 31.5451 109.665 32.4018 109.665 34.0385C109.665 35.7305 108.538 36.6411 106.414 36.6411Z"
        fill="#1A1918"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M90.4839 45.7197H99.9176V42.8713H93.8077V38.3298H99.6923V35.4802H93.8077V31.746H99.9176V28.8951H90.4839V45.7197Z"
        fill="#1A1918"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M80.7677 40.1959L76.2205 28.8951H72.5864L79.8236 46.1512H81.613L88.9799 28.8951H85.3742L80.7677 40.1959Z"
        fill="#1A1918"
      />
      <path
        d="M64.6178 46.7197C69.7118 46.7197 73.8414 42.6454 73.8414 37.6197C73.8414 32.5939 69.7118 28.5197 64.6178 28.5197C59.5238 28.5197 55.3943 32.5939 55.3943 37.6197C55.3943 42.6454 59.5238 46.7197 64.6178 46.7197Z"
        fill={`url(#${gradientId})`}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M41.2231 37.3191C41.2231 42.2643 45.159 46.0986 50.224 46.0986C51.6556 46.0986 52.8817 45.8211 54.3943 45.1184V41.2555C53.0642 42.5685 51.8869 43.0982 50.3788 43.0982C47.0287 43.0982 44.651 40.7017 44.651 37.2944C44.651 34.0645 47.1038 31.5165 50.224 31.5165C51.8104 31.5165 53.0115 32.0749 54.3943 33.4093V29.5483C52.9344 28.8177 51.7334 28.5148 50.3024 28.5148C45.2631 28.5148 41.2231 32.4272 41.2231 37.3191Z"
        fill="#1A1918"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M35.2687 35.3515C33.2725 34.6229 32.6868 34.1419 32.6868 33.2332C32.6868 32.173 33.731 31.3683 35.1646 31.3683C36.1614 31.3683 36.9803 31.772 37.8467 32.7307L39.5873 30.4824C38.157 29.248 36.446 28.6169 34.5763 28.6169C31.5589 28.6169 29.2576 30.6839 29.2576 33.4379C29.2576 35.7558 30.3295 36.9421 33.453 38.0516C34.7555 38.5047 35.4182 38.8063 35.7529 39.0097C36.417 39.4381 36.7497 40.0439 36.7497 40.7504C36.7497 42.1135 35.6515 43.1236 34.1671 43.1236C32.5807 43.1236 31.3032 42.341 30.537 40.8798L28.3879 42.9214C29.9204 45.1405 31.7611 46.124 34.2923 46.124C37.7485 46.124 40.1736 43.8568 40.1736 40.5996C40.1736 37.9268 39.0523 36.7165 35.2687 35.3515Z"
        fill="#1A1918"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M23.8091 28.8951H27.1355V45.7197H23.8091V28.8951Z"
        fill="#1A1918"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.1242 28.8951H8.2417V45.7197H13.0985C15.6811 45.7197 17.5456 45.1184 19.1828 43.7775C21.1283 42.1889 22.2786 39.7949 22.2786 37.319C22.2786 32.3537 18.5187 28.8951 13.1242 28.8951ZM17.01 41.5336C15.9644 42.4651 14.6073 42.8713 12.4582 42.8713H11.5655V31.746H12.4582C14.6073 31.746 15.9111 32.1249 17.01 33.1064C18.1603 34.1171 18.8521 35.683 18.8521 37.2943C18.8521 38.9096 18.1603 40.5235 17.01 41.5336Z"
        fill="#1A1918"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M115.21 29.5275C115.21 29.233 115.005 29.0712 114.643 29.0712H114.162V30.5499H114.52V29.9766L114.939 30.5499H115.376L114.883 29.9402C115.094 29.8843 115.21 29.7329 115.21 29.5275ZM114.58 29.7296H114.52V29.3429H114.584C114.761 29.3429 114.853 29.4059 114.853 29.5327C114.853 29.664 114.76 29.7296 114.58 29.7296Z"
        fill="#1A1918"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M114.715 28.5187C113.987 28.5187 113.41 29.092 113.41 29.8077C113.41 30.5233 113.994 31.0973 114.715 31.0973C115.424 31.0973 116.005 30.5175 116.005 29.8077C116.005 29.1018 115.424 28.5187 114.715 28.5187ZM114.71 30.8672C114.138 30.8672 113.669 30.3966 113.669 29.8096C113.669 29.2207 114.132 28.7508 114.71 28.7508C115.28 28.7508 115.745 29.2318 115.745 29.8096C115.745 30.3914 115.28 30.8672 114.71 30.8672Z"
        fill="#1A1918"
      />
      <defs>
        <radialGradient
          id={gradientId}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(71.5 44) rotate(-142.431) scale(16.4012 16.1816)"
        >
          <stop stopColor="#F59900" />
          <stop offset="0.210082" stopColor="#F39501" />
          <stop offset="0.908163" stopColor="#CE3C0B" />
          <stop offset="1" stopColor="#A4420A" />
        </radialGradient>
      </defs>
    </svg>
  );
}
